/**
 * Ampela certificate HTML template + fill helpers.
 * Matches the framed Ampela specimen: dotted BG, magenta border, centered header.
 * A4 portrait (794×1123 @ 96dpi).
 */

export const CERT_A4_WIDTH_PX = 794;
export const CERT_A4_HEIGHT_PX = 1123;

const AMPELA_LOGO =
  "https://mfe-assets-custom-bucket.s3.af-south-1.amazonaws.com/ampela/logo.png";

const S3_CERT =
  "https://mfe-assets-common-bucket.s3.af-south-1.amazonaws.com/v2-assets/admin/certificate";

const CERT_BG = `${S3_CERT}/certificate_template.png`;
const CERT_SHADOW = `${S3_CERT}/certificate_shadow.png`;

/** Inline SVG icons as data-URIs so TipTap + html2canvas both keep them. */
const svgDataUri = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

const ICON_PIN = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#E40473"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"/></svg>`
);
const ICON_PHONE = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#222222"><path d="M6.62 10.79a15.15 15.15 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.4 21 3 13.6 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z"/></svg>`
);
const ICON_MAIL = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#222222"><path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z"/></svg>`
);

const iconImg = (src) =>
  `<img src="${src}" alt="" width="16" height="16" style="width:16px;height:16px;vertical-align:middle;margin-right:8px;display:inline-block;" />`;

/**
 * Default certificate HTML — Ampela framed specimen (A4).
 *
 * @returns {string}
 */
export function getDefaultCertificateHtml() {
  const logoSrc = process.env.REACT_APP_LOGO_URL || AMPELA_LOGO;
  const color = "#E40473";

  return `<div data-cert="ampela-root" data-cert-ver="5" style="width:${CERT_A4_WIDTH_PX}px;height:${CERT_A4_HEIGHT_PX}px;min-height:${CERT_A4_HEIGHT_PX}px;margin:0 auto;background:#ffffff;color:#222222;font-family:Montserrat,Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;box-sizing:border-box;overflow:hidden;position:relative;">
  <div data-cert="frame" style="position:relative;width:100%;height:${CERT_A4_HEIGHT_PX}px;min-height:${CERT_A4_HEIGHT_PX}px;box-sizing:border-box;overflow:hidden;">
    <img src="${CERT_BG}" alt="" data-cert="bg" crossorigin="anonymous" style="position:absolute;left:0;top:0;width:100%;height:100%;object-fit:cover;object-position:center;z-index:0;display:block;border:0;" />
    <div data-cert="inner" style="position:relative;z-index:1;margin:28px;border:2px solid ${color};min-height:calc(${CERT_A4_HEIGHT_PX}px - 56px);height:calc(${CERT_A4_HEIGHT_PX}px - 56px);box-sizing:border-box;display:flex;flex-direction:column;background:rgba(255,255,255,0.55);">
      <div data-cert="body" style="flex:1;padding:36px 40px 28px;box-sizing:border-box;display:flex;flex-direction:column;">
        <div data-cert="main" style="flex:1;">
          <p style="margin:8px 0 28px;text-align:center;line-height:0;"><img src="${logoSrc}" alt="ampela" width="120" crossorigin="anonymous" style="height:90px;width:auto;max-width:220px;object-fit:contain;display:inline-block;"></p>
          <p style="margin:0;text-align:center;font-size:30px;font-weight:700;color:#222222;line-height:1.15;">Certificat de réalisation</p>
          <div data-cert="title-accent" style="width:0;height:0;margin:14px auto 28px;border-left:150px solid transparent;border-right:150px solid transparent;border-top:5px solid ${color};"></div>
          <p style="margin:0 0 8px;text-align:left;">Participant : <strong>{{STUDENT_NAME}}</strong></p>
          <p style="margin:0 0 8px;text-align:left;">Intitulé de la formation : <strong>Formation 100% en ligne - {{COURSE_TITLE}}</strong></p>
          <p style="margin:0 0 8px;text-align:left;">Dates de la session : du <strong>{{RUN_START}}</strong> au <strong>{{RUN_END}}</strong></p>
          <p style="margin:0 0 24px;text-align:left;">Lieu de formation : <strong>Distanciel</strong></p>
          <p style="margin:0 0 10px;text-align:left;"><strong>Objectifs de la formation :</strong></p>
          <div data-cert="objectives" style="text-align:left;">
            <p style="margin:0 0 6px;">▷ objective 1</p>
            <p style="margin:0 0 6px;">▷ objective 2</p>
            <p style="margin:0 0 6px;">▷ objective 3</p>
            <p style="margin:0 0 6px;">▷ objective 4</p>
          </div>
          <p style="margin:28px 0 6px;text-align:center;">Nature de l'action concourant au développement des compétences:</p>
          <p style="margin:0 0 24px;text-align:center;"><strong>Action de formation</strong></p>
          <div data-cert="stamp-wrap" style="position:relative;margin:24px 0;height:128px;">
            <img src="${CERT_SHADOW}" alt="" crossorigin="anonymous" style="display:block;width:100%;height:128px;object-fit:fill;" />
            <div data-cert="stamp" style="position:absolute;left:0;top:0;width:calc(100% - 10px);height:120px;padding:16px 10px;border-radius:8px;background:${color};color:#ffffff;text-align:left;box-sizing:border-box;display:flex;flex-direction:column;justify-content:center;line-height:1.45;">
              <p style="margin:0;color:#ffffff;">{{PLATFORM_NAME}} atteste que <strong>{{STUDENT_NAME}}</strong> a suivi la formation <strong>{{COURSE_TITLE}}</strong></p>
              <p style="margin:0;color:#ffffff;">du {{RUN_START}} au {{RUN_END}}.</p>
              <p style="margin:0;color:#ffffff;"><strong>Nombre d'heures réalisées :</strong> {{DURATION}} h</p>
            </div>
          </div>
          <p style="margin:8px 0 0;text-align:center;line-height:1.4;">{{COMMENT}}</p>
        </div>
        <div data-cert="footer-row" style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-top:16px;">
          <div data-cert="contact" style="border:1px solid ${color};padding:12px 14px;font-size:12px;min-width:210px;box-sizing:border-box;background:#ffffff;">
            <p style="margin:0 0 8px;">${iconImg(ICON_PIN)}{{LOCATION}}</p>
            <p style="margin:0 0 8px;">${iconImg(ICON_PHONE)}{{PHONE}}</p>
            <p style="margin:0 0 8px;">${iconImg(ICON_MAIL)}{{EMAIL}}</p>
            <p style="margin:0;"><strong>SIRET :</strong> {{SIRET}}</p>
          </div>
          <div data-cert="sign-block" style="text-align:center;margin-left:auto;min-width:180px;">
            <p style="margin:0 0 6px;">{{SIGN_TOP}} {{CREATE_DATE}}</p>
            <div data-cert="signature-slot" style="min-height:64px;margin:16px 0;text-align:center;"></div>
            <p style="margin:0;">{{SIGN_BOTTOM}}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>`;
}

/**
 * @param {string} content
 * @returns {boolean}
 */
export function isBlankCertificateHtml(content) {
  const raw = String(content || "").trim();
  if (!raw) return true;
  const text = raw
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, "")
    .trim();
  return !text;
}

/**
 * @param {string} content
 * @returns {boolean}
 */
/**
 * Only blank/empty HTML should fall back to the default template.
 * Never treat TipTap-edited HTML as "needs default" — that wiped saves on refresh.
 *
 * @param {string} content
 * @returns {boolean}
 */
export function needsCertificateDefault(content) {
  return isBlankCertificateHtml(content);
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * @param {string} url
 * @returns {string}
 */
function buildSignatureSlotHtml(url) {
  const clean = String(url || "").trim().split("?")[0];
  if (!clean || clean.startsWith("data:image/gif")) {
    return `<div data-cert="signature-slot" style="min-height:56px;margin:10px 0;text-align:center;"></div>`;
  }
  return `<div data-cert="signature-slot" style="min-height:56px;margin:10px 0;text-align:center;"><img src="${escapeHtml(clean)}" alt="signature" crossorigin="anonymous" width="180" style="height:56px;width:auto;max-width:200px;object-fit:contain;"></div>`;
}

/**
 * Replace placeholders and inject signature image when available.
 *
 * @param {string} template
 * @param {Record<string, string|number|null|undefined>} values
 * @returns {string}
 */
export function fillHtmlTemplate(template, values = {}) {
  if (!template) return "";
  let result = String(template);
  const data = { ...(values || {}) };

  if (data.OBJECTIVES_HTML != null) {
    const objectivesHtml = String(data.OBJECTIVES_HTML);
    if (/data-cert=["']objectives["']/.test(result)) {
      result = result.replace(
        /<div[^>]*data-cert=["']objectives["'][^>]*>[\s\S]*?<\/div>/i,
        `<div data-cert="objectives" style="text-align:left;">${objectivesHtml}</div>`
      );
    }
    delete data.OBJECTIVES_HTML;
  }

  const signatureUrl = data.SIGNATURE_URL;
  delete data.SIGNATURE_URL;
  if (/data-cert=["']signature-slot["']/.test(result)) {
    result = result.replace(
      /<div[^>]*data-cert=["']signature-slot["'][^>]*>[\s\S]*?<\/div>/i,
      buildSignatureSlotHtml(signatureUrl)
    );
  } else if (signatureUrl) {
    data.SIGNATURE_URL = String(signatureUrl).split("?")[0];
  }

  Object.keys(data).forEach((key) => {
    const raw = data[key];
    const val = raw == null ? "" : String(raw);
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), val);
  });
  return result;
}

/**
 * @param {string[]|string} objectives
 * @returns {string}
 */
export function buildObjectivesHtml(objectives) {
  const list = Array.isArray(objectives)
    ? objectives
    : String(objectives || "")
        .split("\n")
        .map((v) => v.trim())
        .filter(Boolean);
  if (!list.length) {
    return `<p style="margin:0 0 6px;color:#666666;">—</p>`;
  }
  return list
    .map((item) => `<p style="margin:0 0 6px;">▷ ${escapeHtml(item)}</p>`)
    .join("");
}

/**
 * @param {object} certVar
 * @param {{ colorMain1?: string, logoUrl?: string, signatureUrl?: string, platformName?: string }} meta
 * @returns {Record<string, string>}
 */
function buildSharedValues(certVar = {}, meta = {}) {
  const signatureUrl = (meta.signatureUrl || certVar.signature_url || "").split("?")[0];
  const logoUrl = (meta.logoUrl || process.env.REACT_APP_LOGO_URL || AMPELA_LOGO).split("?")[0];
  return {
    PLATFORM_NAME: meta.platformName || process.env.REACT_APP_SITE_DISPLAY_NAME || "Ampela",
    LOCATION: certVar.location || "",
    PHONE: certVar.phone_number || "",
    EMAIL: certVar.contact_email || "",
    SIRET: certVar.siret_number || "",
    SIGN_TOP: certVar.sign_top || "",
    SIGN_BOTTOM: certVar.sign_bottom || "",
    COMMENT: certVar.comment || "",
    SIGNATURE_URL: signatureUrl || "",
    LOGO_URL: logoUrl,
    COLOR_MAIN1: meta.colorMain1 || "#E40473",
  };
}

/**
 * @param {object} certVar
 * @param {{ colorMain1?: string, logoUrl?: string, signatureUrl?: string, platformName?: string }} meta
 * @returns {Record<string, string>}
 */
export function buildCertificateSpecimenValues(certVar = {}, meta = {}) {
  return {
    STUDENT_NAME: "{{STUDENT_NAME}}",
    COURSE_TITLE: "{{COURSE_TITLE}}",
    RUN_START: "{{RUN_START}}",
    RUN_END: "{{RUN_END}}",
    DURATION: "{{DURATION}}",
    CREATE_DATE: "{{CREATE_DATE}}",
    OBJECTIVES_HTML: buildObjectivesHtml(["objective 1", "objective 2", "objective 3", "objective 4"]),
    ...buildSharedValues(certVar, meta),
  };
}

/**
 * @param {object} args
 * @returns {Record<string, string>}
 */
export function buildCertificateLearnerValues({
  studentName,
  courseTitle,
  runStart,
  runEnd,
  duration,
  created,
  objectives,
  certVar = {},
  meta = {},
}) {
  return {
    STUDENT_NAME: studentName || "",
    COURSE_TITLE: courseTitle || "",
    RUN_START: runStart || "",
    RUN_END: runEnd || "",
    DURATION: duration == null ? "" : String(duration),
    CREATE_DATE: created || "",
    OBJECTIVES_HTML: buildObjectivesHtml(objectives || []),
    ...buildSharedValues(certVar, meta),
  };
}

/**
 * @param {string} content
 * @returns {string}
 */
export function resolveCertificateContent(content) {
  if (needsCertificateDefault(content)) return getDefaultCertificateHtml();
  return String(content);
}
